import { useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Layout } from '@/components/Layout';
import { RequireAuth } from '@/components/RequireAuth';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { LoginPage } from '@/features/auth/LoginPage';
import { registerApiNavigator } from '@/lib/api-client';

/**
 * iCGChat console — root component.
 *
 * M5-T2 splits the routes into two regions: `/login` (unauthenticated, the
 * only public surface) and `/*` (everything else, gated by RequireAuth). The
 * three-column Layout lives behind the gate. The Toaster mounts here so
 * `sonner.toast(...)` calls from anywhere — including the axios 401
 * interceptor — surface in the same place.
 *
 * `registerApiNavigator` hands the axios 401 path a react-router `navigate`
 * function so a session-expiry redirect feels SPA-native (no full reload).
 */
export function App(): React.JSX.Element {
  const navigate = useNavigate();

  useEffect(() => {
    registerApiNavigator(navigate);
  }, [navigate]);

  return (
    // TooltipProvider hosts the (Radix) tooltip portal context for the whole
    // app. `delayDuration={200}` keeps hovers snappy; the default 700ms feels
    // like a delay-glitch.
    //
    // ErrorBoundary wraps Routes (inside TooltipProvider) so tooltip context
    // is available in the fallback, but outside Toaster so the toast portal
    // mounts unaffected even when the boundary has caught an error.
    <TooltipProvider delayDuration={200}>
      <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Layout />
              </RequireAuth>
            }
          />
        </Routes>
      </ErrorBoundary>
      <Toaster />
    </TooltipProvider>
  );
}
